import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ChevronDown, ChevronUp, ShieldCheck, Building2, Search, Loader2,
  Save, Trash2, TrendingDown, TrendingUp, Minus, ArrowRight,
  BookOpen, Zap, BarChart3, FileText, AlertTriangle, Info, Calculator
} from "lucide-react";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/risk-simulator`;

const DEFAULTS = {
  cash_balance: 5000000, monthly_burn: 400000, funding_stage: "Series A",
  industry: "SaaS / Software", employee_count: 30, monthly_spend: 75000,
  annual_revenue: 2000000, mrr: 170000, revenue_model: "Recurring",
  profitable: "no", total_funding: 10000000, investor_tier: "Tier 1",
  months_since_round: 10, outstanding_debt: 0, existing_credit: 0,
  prior_defaults: "none", paydex: 0, intelliscore: 0,
  years_in_business: 3, entity_type: "C-Corp", min_balance_90d: 3500000, nsf_events: 0,
};

const fmtCurrency = (v) => {
  if (v == null) return "—";
  if (Math.abs(v) >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v.toLocaleString()}`;
};

const RISK_COLORS = {
  "Lowest Risk": "#16a34a", "Low Risk": "#65a30d", "Moderate Risk": "#ca8a04",
  "Elevated Risk": "#ea580c", "Critical Risk": "#dc2626",
};

const RISK_ACTIONS = {
  "Lowest Risk": "Auto-approve. Standard limits.", "Low Risk": "Auto-approve. Periodic monitoring.",
  "Moderate Risk": "Auto-approve with conservative limit.", "Elevated Risk": "Manual review required.",
  "Critical Risk": "Decline recommended. VP approval needed.",
};

export default function RiskSimulator() {
  const [inputs, setInputs] = useState(DEFAULTS);
  const [result, setResult] = useState(null);
  const [refData, setRefData] = useState(null);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [mode, setMode] = useState("manual");
  const [hrisEin, setHrisEin] = useState("");
  const [hrisLoading, setHrisLoading] = useState(false);
  const [hrisFields, setHrisFields] = useState([]);
  const [hrisCompany, setHrisCompany] = useState("");
  const [snapshots, setSnapshots] = useState([]);
  const [snapName, setSnapName] = useState("");
  const [showInternals, setShowInternals] = useState(false);
  const sessionId = useRef(`sim-${Date.now()}`);
  const debounceRef = useRef(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/reference-data`),
      axios.get(`${API}/presets`),
    ]).then(([ref, pre]) => { setRefData(ref.data); setPresets(pre.data); }).catch(console.error);
  }, []);

  const calculate = useCallback(async (inp) => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/calculate`, inp);
      setResult(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  // Debounced auto-calculate on input change
  useEffect(() => {
    if (!refData) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => calculate(inputs), 80);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [inputs, refData, calculate]);

  const setField = (key, val) => setInputs(prev => ({ ...prev, [key]: val }));

  const loadPreset = async (id) => {
    try {
      const r = await axios.get(`${API}/presets/${id}`);
      setInputs(r.data);
      setHrisFields([]);
      setHrisCompany("");
      setMode("manual");
    } catch (e) { console.error(e); }
  };

  const lookupHris = async () => {
    if (!hrisEin) return;
    setHrisLoading(true);
    try {
      const r = await axios.get(`${API}/hris-lookup?ein=${hrisEin}`);
      if (r.data.found) {
        setInputs(prev => ({ ...prev, ...r.data.inputs }));
        setHrisFields(r.data.hris_fields || []);
        setHrisCompany(r.data.company_name || "");
      }
    } catch (e) { console.error(e); }
    finally { setHrisLoading(false); }
  };

  const saveSnapshot = async () => {
    if (!result) return;
    const name = snapName || `Scenario ${snapshots.length + 1}`;
    try {
      const r = await axios.post(`${API}/snapshots`, {
        session_id: sessionId.current, name,
        data: { pd: result.pd, risk_class: result.risk_class, ecl: result.ecl, credit_limit: result.credit_limit, ead: result.ead, lgd: result.lgd }
      });
      setSnapshots(prev => [...prev, r.data]);
      setSnapName("");
    } catch (e) { console.error(e); }
  };

  const deleteSnapshot = async (id) => {
    try {
      await axios.delete(`${API}/snapshots/${sessionId.current}/${id}`);
      setSnapshots(prev => prev.filter(s => s.id !== id));
    } catch (e) { console.error(e); }
  };

  if (!refData) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#502D3C]" /></div>;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="risk-simulator-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-[#E2E8F0] px-6 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-[#502D3C] tracking-tight" data-testid="page-title">Credit Risk Simulator</h1>
            <p className="text-[10px] text-[#64748B]">Estimate PD, EAD, LGD, ECL, and credit limit in real-time</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Mode toggle */}
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              <button onClick={() => setMode("manual")} className={cn("px-3 py-1 rounded-md text-xs font-medium transition-colors", mode === "manual" ? "bg-white text-[#502D3C] shadow-sm" : "text-[#64748B]")} data-testid="mode-manual">Manual Entry</button>
              <button onClick={() => setMode("hris")} className={cn("px-3 py-1 rounded-md text-xs font-medium transition-colors", mode === "hris" ? "bg-white text-[#502D3C] shadow-sm" : "text-[#64748B]")} data-testid="mode-hris">HRIS Lookup</button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex" style={{ height: "calc(100vh - 60px)" }}>
        {/* ===== LEFT PANEL: INPUTS ===== */}
        <div className="w-[420px] flex-shrink-0 border-r border-[#E2E8F0] bg-white overflow-y-auto" data-testid="input-panel">
          <div className="p-4 space-y-4">
            {/* HRIS Lookup */}
            {mode === "hris" && (
              <Card className="border-[#FFCC1C]/50 bg-[#FFFDF0]" data-testid="hris-lookup">
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-[#502D3C]" />
                    <span className="text-xs font-semibold text-[#502D3C]">HRIS Auto-Fill</span>
                  </div>
                  <div className="flex gap-2">
                    <Input value={hrisEin} onChange={e => setHrisEin(e.target.value)} placeholder="Enter EIN (e.g. 87-1234567)" className="h-8 text-xs font-mono" data-testid="hris-ein" />
                    <Button variant="outline" size="sm" className="h-8 text-xs text-[#502D3C] border-[#502D3C]/30" onClick={lookupHris} disabled={hrisLoading} data-testid="hris-lookup-btn">
                      {hrisLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Search className="h-3 w-3" />}
                    </Button>
                  </div>
                  {hrisCompany && <p className="text-[10px] text-green-600"><ShieldCheck className="h-3 w-3 inline mr-1" />Connected: {hrisCompany}</p>}
                  <div className="flex gap-1 flex-wrap">
                    {["87-1234567", "20-5678901", "45-9012345"].map(ein => (
                      <button key={ein} onClick={() => setHrisEin(ein)} className="text-[8px] text-[#502D3C] bg-white border rounded px-1.5 py-0.5 font-mono hover:bg-gray-50" data-testid={`demo-${ein}`}>{ein}</button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quick Presets */}
            <div>
              <p className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wider mb-2">Quick Presets</p>
              <div className="flex gap-1.5 flex-wrap" data-testid="presets">
                {presets.map(p => (
                  <button key={p.id} onClick={() => loadPreset(p.id)} className="text-[10px] px-2.5 py-1.5 rounded-md border border-gray-200 text-[#502D3C] hover:bg-[#502D3C]/5 hover:border-[#502D3C]/20 transition-colors" data-testid={`preset-${p.id}`}>{p.label}</button>
                ))}
              </div>
            </div>

            <Separator />

            {/* Core Inputs */}
            <p className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wider">Core Inputs</p>

            <SliderField label="Cash Balance" value={inputs.cash_balance} onChange={v => setField("cash_balance", v)} min={0} max={100000000} step={100000} format={fmtCurrency} verified={hrisFields.includes("cash_balance")} testId="cash-balance" />
            <SliderField label="Monthly Burn Rate" value={inputs.monthly_burn} onChange={v => setField("monthly_burn", v)} min={0} max={5000000} step={25000} format={fmtCurrency} verified={hrisFields.includes("monthly_burn")} testId="monthly-burn" />
            <DropdownField label="Funding Stage" value={inputs.funding_stage} options={refData.stages} onChange={v => setField("funding_stage", v)} testId="funding-stage" />
            <DropdownField label="Industry" value={inputs.industry} options={refData.industries} onChange={v => setField("industry", v)} testId="industry" />
            <NumberField label="Employee Count" value={inputs.employee_count} onChange={v => setField("employee_count", parseInt(v) || 0)} verified={hrisFields.includes("employee_count")} testId="employees" />
            <SliderField label="Est. Monthly Card Spend" value={inputs.monthly_spend} onChange={v => setField("monthly_spend", v)} min={5000} max={1000000} step={5000} format={fmtCurrency} testId="monthly-spend" />

            <Separator />

            {/* Advanced toggle */}
            <button onClick={() => setAdvanced(!advanced)} className="flex items-center gap-1.5 text-xs text-[#502D3C] hover:text-[#502D3C]/80 font-medium" data-testid="toggle-advanced">
              {advanced ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              {advanced ? "Hide" : "Show"} Advanced Inputs ({advanced ? "16" : "16"} fields)
            </button>

            {advanced && (
              <div className="space-y-4 pt-1" data-testid="advanced-inputs">
                <p className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wider">Financial</p>
                <SliderField label="Min Balance (90d)" value={inputs.min_balance_90d} onChange={v => setField("min_balance_90d", v)} min={0} max={100000000} step={100000} format={fmtCurrency} verified={hrisFields.includes("min_balance_90d")} testId="min-balance" />
                <DropdownField label="NSF/Overdraft Events" value={String(inputs.nsf_events)} options={["0", "1", "2", "3"]} onChange={v => setField("nsf_events", parseInt(v))} testId="nsf" />
                <SliderField label="Annual Revenue" value={inputs.annual_revenue} onChange={v => setField("annual_revenue", v)} min={0} max={200000000} step={100000} format={fmtCurrency} testId="revenue" />
                <SliderField label="MRR" value={inputs.mrr} onChange={v => setField("mrr", v)} min={0} max={20000000} step={10000} format={fmtCurrency} testId="mrr" />
                <DropdownField label="Revenue Model" value={inputs.revenue_model} options={refData.revenue_models} onChange={v => setField("revenue_model", v)} testId="rev-model" />
                <DropdownField label="Profitable?" value={inputs.profitable} options={["no", "near_breakeven", "yes"]} labels={["No", "Near Breakeven", "Yes"]} onChange={v => setField("profitable", v)} testId="profitable" />

                <Separator />
                <p className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wider">Capital Structure</p>
                <SliderField label="Total Funding Raised" value={inputs.total_funding} onChange={v => setField("total_funding", v)} min={0} max={500000000} step={500000} format={fmtCurrency} testId="total-funding" />
                <DropdownField label="Lead Investor Tier" value={inputs.investor_tier} options={refData.investor_tiers} onChange={v => setField("investor_tier", v)} testId="investor-tier" />
                <SliderField label="Months Since Last Round" value={inputs.months_since_round} onChange={v => setField("months_since_round", v)} min={0} max={48} step={1} format={v => `${v} mo`} testId="months-since" />
                <SliderField label="Outstanding Debt" value={inputs.outstanding_debt} onChange={v => setField("outstanding_debt", v)} min={0} max={50000000} step={100000} format={fmtCurrency} testId="debt" />
                <SliderField label="Existing Credit Lines" value={inputs.existing_credit} onChange={v => setField("existing_credit", v)} min={0} max={10000000} step={50000} format={fmtCurrency} testId="credit-lines" />
                <DropdownField label="Prior Defaults" value={inputs.prior_defaults} options={refData.default_options} labels={["None", "Resolved (>2yr)", "Recent (<2yr)"]} onChange={v => setField("prior_defaults", v)} testId="defaults" />

                <Separator />
                <p className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wider">Bureau & Company</p>
                <SliderField label="D&B PAYDEX" value={inputs.paydex} onChange={v => setField("paydex", v)} min={0} max={100} step={1} format={v => v === 0 ? "N/A (thin)" : String(v)} testId="paydex" />
                <SliderField label="Experian Intelliscore" value={inputs.intelliscore} onChange={v => setField("intelliscore", v)} min={0} max={100} step={1} format={v => v === 0 ? "N/A" : String(v)} testId="intelliscore" />
                <NumberField label="Years in Business" value={inputs.years_in_business} onChange={v => setField("years_in_business", parseInt(v) || 0)} verified={hrisFields.includes("years_in_business")} testId="years" />
                <DropdownField label="Entity Type" value={inputs.entity_type} options={refData.entity_types} onChange={v => setField("entity_type", v)} verified={hrisFields.includes("entity_type")} testId="entity-type" />
              </div>
            )}
          </div>
        </div>

        {/* ===== RIGHT PANEL: OUTPUTS ===== */}
        <div className="flex-1 overflow-y-auto" data-testid="output-panel">
          {result ? (
            <div className="p-5 space-y-5">
              {/* 7A: Core Metrics */}
              <div className="grid grid-cols-4 gap-3" data-testid="core-metrics">
                <MetricCard label="Probability of Default" value={`${result.pd}%`} color="#3b82f6" subtitle={`Log-odds: ${result.log_odds}`} />
                <MetricCard label="Exposure at Default" value={fmtCurrency(result.ead)} color="#502D3C" subtitle={`CCF: ${(result.ead_breakdown.ccf * 100).toFixed(0)}%`} />
                <MetricCard label="Loss Given Default" value={`${result.lgd}%`} color="#ec4899" subtitle={`Recovery: ${(100 - result.lgd).toFixed(1)}%`} />
                <MetricCard label="Expected Credit Loss" value={fmtCurrency(result.ecl)} color="#f59e0b" subtitle={`PD x EAD x LGD`} />
              </div>

              {/* 7B: Risk Classification + Credit Limit */}
              <div className="grid grid-cols-2 gap-4">
                <Card data-testid="risk-gauge">
                  <CardContent className="p-4">
                    <p className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wider mb-3">Risk Classification</p>
                    <RiskGauge pd={result.pd} riskClass={result.risk_class} />
                    <p className="text-xs text-[#64748B] mt-2 text-center">{RISK_ACTIONS[result.risk_class]}</p>
                  </CardContent>
                </Card>
                <Card data-testid="credit-limit-card">
                  <CardContent className="p-4">
                    <p className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wider mb-2">Approved Credit Limit</p>
                    <p className="text-3xl font-bold text-[#502D3C]">{fmtCurrency(result.credit_limit)}</p>
                    <div className="mt-3">
                      <div className="flex justify-between text-[9px] text-[#94A3B8] mb-1"><span>$0</span><span>{fmtCurrency(result.segment_cap)}</span></div>
                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(100, (result.credit_limit / result.segment_cap) * 100)}%`, backgroundColor: RISK_COLORS[result.risk_class] }} />
                      </div>
                    </div>
                    <div className="mt-3 space-y-1">
                      <LimitRow label="Cash-based cap" value={fmtCurrency(result.limit_breakdown.cash_based)} binding={result.limit_breakdown.binding_constraint === "cash_based"} />
                      <LimitRow label="Spend-based cap" value={fmtCurrency(result.limit_breakdown.spend_based)} binding={result.limit_breakdown.binding_constraint === "spend_based"} />
                      <LimitRow label="Segment cap" value={fmtCurrency(result.limit_breakdown.segment_cap)} binding={result.limit_breakdown.binding_constraint === "segment_cap"} />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* 7C: PD Factor Breakdown */}
              <Card data-testid="factor-breakdown">
                <CardHeader className="pb-1 pt-3 px-4">
                  <CardTitle className="text-xs font-semibold text-[#502D3C] flex items-center gap-1.5"><BarChart3 className="h-3.5 w-3.5" />PD Factor Breakdown</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-3">
                  <div className="space-y-1.5">
                    {result.factors.map((f, i) => (
                      <FactorRow key={i} factor={f} maxImpact={Math.max(...result.factors.map(f => Math.abs(f.contribution)))} />
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* 7D: Sensitivity Analysis */}
              <Card data-testid="sensitivity-analysis">
                <CardHeader className="pb-1 pt-3 px-4">
                  <CardTitle className="text-xs font-semibold text-[#502D3C] flex items-center gap-1.5"><Zap className="h-3.5 w-3.5" />Sensitivity Analysis</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-3">
                  <div className="grid grid-cols-3 gap-2.5">
                    {result.sensitivity.map(s => (
                      <ScenarioCard key={s.id} scenario={s} basePd={result.pd} baseEcl={result.ecl} baseLimit={result.credit_limit} />
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* 7E: Scenario Snapshots */}
              <Card data-testid="snapshots">
                <CardHeader className="pb-1 pt-3 px-4">
                  <CardTitle className="text-xs font-semibold text-[#502D3C] flex items-center gap-1.5"><Save className="h-3.5 w-3.5" />Scenario Snapshots</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-3">
                  <div className="flex gap-2 mb-3">
                    <Input value={snapName} onChange={e => setSnapName(e.target.value)} placeholder="Scenario name..." className="h-8 text-xs flex-1" data-testid="snap-name" />
                    <Button variant="outline" size="sm" className="h-8 text-xs text-[#502D3C] border-[#502D3C]/30" onClick={saveSnapshot} data-testid="save-snap-btn"><Save className="h-3 w-3 mr-1" />Save Current</Button>
                  </div>
                  {snapshots.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead><tr className="border-b">{["Name", "PD", "Risk Class", "ECL", "Credit Limit", ""].map(h => <th key={h} className="text-left py-1.5 text-[9px] font-semibold text-[#64748B] uppercase">{h}</th>)}</tr></thead>
                        <tbody>
                          {snapshots.map(s => (
                            <tr key={s.id} className="border-b border-gray-50">
                              <td className="py-1.5 font-medium text-[#502D3C]">{s.name}</td>
                              <td className="py-1.5">{s.pd}%</td>
                              <td className="py-1.5"><Badge className="text-[8px] h-4 border" style={{ backgroundColor: RISK_COLORS[s.risk_class] + '15', borderColor: RISK_COLORS[s.risk_class] + '40', color: RISK_COLORS[s.risk_class] }}>{s.risk_class}</Badge></td>
                              <td className="py-1.5">{fmtCurrency(s.ecl)}</td>
                              <td className="py-1.5 font-medium">{fmtCurrency(s.credit_limit)}</td>
                              <td className="py-1.5"><button onClick={() => deleteSnapshot(s.id)} className="text-[#94A3B8] hover:text-red-500"><Trash2 className="h-3 w-3" /></button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : <p className="text-[10px] text-[#94A3B8] text-center py-3">No snapshots saved. Adjust inputs and save to compare scenarios.</p>}
                </CardContent>
              </Card>

              {/* 7F: Model Internals */}
              <Card data-testid="model-internals">
                <CardHeader className="pb-0 pt-3 px-4 cursor-pointer" onClick={() => setShowInternals(!showInternals)}>
                  <CardTitle className="text-xs font-semibold text-[#502D3C] flex items-center gap-1.5">
                    <Calculator className="h-3.5 w-3.5" />Model Internals
                    {showInternals ? <ChevronUp className="h-3 w-3 ml-auto" /> : <ChevronDown className="h-3 w-3 ml-auto" />}
                  </CardTitle>
                </CardHeader>
                {showInternals && (
                  <CardContent className="px-4 pb-3 pt-2">
                    <div className="bg-gray-900 rounded-lg p-4 font-mono text-[10px] text-gray-300 leading-relaxed overflow-x-auto whitespace-pre" data-testid="internals-code">
{`═══ PD MODEL (Logistic Regression) ═══
Intercept (β₀):  ${result.intercept}

Factor Contributions:
${result.factors.map(f => `  ${f.name.padEnd(22)} w=${f.weight.toFixed(2)}  score=${f.score >= 0 ? '+' : ''}${f.score.toFixed(2)}  → ${f.contribution >= 0 ? '+' : ''}${f.contribution.toFixed(3)}`).join('\n')}

Profitability adj:    ${result.profit_adjustment >= 0 ? '+' : ''}${result.profit_adjustment.toFixed(2)}
────────────────────────────────────
log_odds = ${result.log_odds.toFixed(4)}
PD = sigmoid(${result.log_odds.toFixed(4)}) = ${result.pd_raw}%
PD (bounded) = ${result.pd}%

═══ RISK CLASSIFICATION ═══
${result.risk_class} (PD ${result.pd < 3 ? '< 3%' : result.pd < 8 ? '3-8%' : result.pd < 15 ? '8-15%' : result.pd < 30 ? '15-30%' : '> 30%'})

═══ EAD MODEL ═══
Billed balance:       ${fmtCurrency(result.ead_breakdown.billed_balance)}
Unbilled charges:     ${fmtCurrency(result.ead_breakdown.unbilled_charges)}
Unused limit:         ${fmtCurrency(result.ead_breakdown.unused_limit)}
CCF (${result.risk_class}):   ${(result.ead_breakdown.ccf * 100).toFixed(0)}%
Projected future use: ${fmtCurrency(result.ead_breakdown.projected_future_use)}
────────────────────────────────────
EAD = ${fmtCurrency(result.ead)}

═══ LGD MODEL ═══
${result.lgd_breakdown.map(l => `  ${l.name.padEnd(20)} ${l.adjustment === 0 ? `base ${l.value}` : `${l.adjustment >= 0 ? '+' : ''}${l.adjustment}%  (${l.value})`}`).join('\n')}
────────────────────────────────────
LGD = ${result.lgd}%

═══ CREDIT LIMIT ═══
Cash-based:  ${fmtCurrency(result.limit_breakdown.cash_based)}  (cash × ${(result.limit_breakdown.coverage_ratio * 100).toFixed(1)}%)
Spend-based: ${fmtCurrency(result.limit_breakdown.spend_based)}  (spend × 2.0)
Segment cap: ${fmtCurrency(result.limit_breakdown.segment_cap)}
Binding:     ${result.limit_breakdown.binding_constraint}
────────────────────────────────────
Approved Limit = ${fmtCurrency(result.credit_limit)}

═══ ECL ═══
ECL = EAD × PD × LGD
    = ${fmtCurrency(result.ead)} × ${result.pd}% × ${result.lgd}%
    = ${fmtCurrency(result.ecl)}`}
                    </div>
                  </CardContent>
                )}
              </Card>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full"><Loader2 className="h-8 w-8 animate-spin text-[#502D3C]" /></div>
          )}
        </div>
      </div>
    </div>
  );
}

// ===== SUB-COMPONENTS =====

function SliderField({ label, value, onChange, min, max, step, format, verified, testId }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-[11px] font-medium text-[#502D3C]">{label}{verified && <Badge className="bg-green-50 text-green-600 border-green-200 text-[7px] h-3.5 ml-1.5"><ShieldCheck className="h-2.5 w-2.5 mr-0.5" />HRIS</Badge>}</Label>
        <span className="text-[11px] font-semibold text-[#502D3C] tabular-nums">{format(value)}</span>
      </div>
      <Slider value={[value]} onValueChange={v => onChange(v[0])} min={min} max={max} step={step} className="w-full" data-testid={testId} />
    </div>
  );
}

function DropdownField({ label, value, options, labels, onChange, verified, testId }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] font-medium text-[#502D3C]">{label}{verified && <Badge className="bg-green-50 text-green-600 border-green-200 text-[7px] h-3.5 ml-1.5"><ShieldCheck className="h-2.5 w-2.5 mr-0.5" />HRIS</Badge>}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-8 text-xs" data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map((o, i) => <SelectItem key={o} value={o}>{labels ? labels[i] : o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function NumberField({ label, value, onChange, verified, testId }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] font-medium text-[#502D3C]">{label}{verified && <Badge className="bg-green-50 text-green-600 border-green-200 text-[7px] h-3.5 ml-1.5"><ShieldCheck className="h-2.5 w-2.5 mr-0.5" />HRIS</Badge>}</Label>
      <Input type="number" value={value} onChange={e => onChange(e.target.value)} className="h-8 text-xs" data-testid={testId} />
    </div>
  );
}

function MetricCard({ label, value, color, subtitle }) {
  return (
    <Card className="overflow-hidden">
      <div className="h-1" style={{ backgroundColor: color }} />
      <CardContent className="p-3">
        <p className="text-[9px] font-semibold text-[#64748B] uppercase tracking-wider">{label}</p>
        <p className="text-2xl font-bold mt-1" style={{ color }}>{value}</p>
        <p className="text-[9px] text-[#94A3B8] mt-0.5">{subtitle}</p>
      </CardContent>
    </Card>
  );
}

function RiskGauge({ pd, riskClass }) {
  const color = RISK_COLORS[riskClass] || "#94A3B8";
  // Map PD to gauge position: 0% = left, 65% = right
  const angle = Math.min(180, (pd / 65) * 180);
  const zones = [
    { pct: 3/65*100, color: "#16a34a" },
    { pct: 5/65*100, color: "#65a30d" },
    { pct: 7/65*100, color: "#ca8a04" },
    { pct: 15/65*100, color: "#ea580c" },
    { pct: 100 - 30/65*100, color: "#dc2626" },
  ];

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-48 h-24 overflow-hidden">
        <svg viewBox="0 0 200 100" className="w-full h-full">
          {/* Background arc */}
          <path d="M 10 95 A 85 85 0 0 1 190 95" fill="none" stroke="#E2E8F0" strokeWidth="12" strokeLinecap="round" />
          {/* Colored segments */}
          <path d="M 10 95 A 85 85 0 0 1 190 95" fill="none" stroke={color} strokeWidth="12" strokeLinecap="round" strokeDasharray={`${(angle / 180) * 267} 267`} className="transition-all duration-700" />
          {/* Needle */}
          <line x1="100" y1="95" x2={100 + 70 * Math.cos(Math.PI - (angle * Math.PI / 180))} y2={95 - 70 * Math.sin(Math.PI - (angle * Math.PI / 180))} stroke={color} strokeWidth="2.5" strokeLinecap="round" className="transition-all duration-700" />
          <circle cx="100" cy="95" r="5" fill={color} className="transition-all duration-700" />
        </svg>
      </div>
      <Badge className="text-xs h-6 px-3 border mt-1" style={{ backgroundColor: color + '15', borderColor: color + '40', color }}>{riskClass}</Badge>
    </div>
  );
}

function FactorRow({ factor, maxImpact }) {
  const maxContrib = maxImpact;
  const barWidth = maxContrib > 0 ? Math.abs(factor.contribution) / maxContrib * 100 : 0;
  const isPositive = factor.contribution < 0; // negative contribution = reduces PD = good
  return (
    <div className="flex items-center gap-2 py-1 text-[11px]">
      <span className="w-[140px] flex-shrink-0 font-medium text-[#502D3C] truncate">{factor.name}</span>
      <span className="w-[100px] flex-shrink-0 text-[#64748B] truncate">{factor.value}</span>
      <div className="flex-1 flex items-center gap-1">
        <div className="flex-1 h-4 relative flex items-center">
          {/* Center line */}
          <div className="absolute left-1/2 w-px h-full bg-gray-200" />
          {/* Bar */}
          {isPositive ? (
            <div className="absolute right-1/2 h-3 rounded-l-sm transition-all duration-300" style={{ width: `${barWidth / 2}%`, backgroundColor: '#16a34a' }} />
          ) : (
            <div className="absolute left-1/2 h-3 rounded-r-sm transition-all duration-300" style={{ width: `${barWidth / 2}%`, backgroundColor: '#dc2626' }} />
          )}
        </div>
      </div>
      <span className={cn("w-[65px] flex-shrink-0 text-right font-medium tabular-nums", factor.contribution < 0 ? "text-green-600" : factor.contribution > 0 ? "text-red-600" : "text-[#64748B]")}>
        {factor.impact_pct > 0 ? "+" : ""}{factor.impact_pct}%
      </span>
    </div>
  );
}

function ScenarioCard({ scenario, basePd, baseEcl, baseLimit }) {
  const pdDir = scenario.pd_delta > 0.5 ? "worse" : scenario.pd_delta < -0.5 ? "better" : "same";
  const limitDir = scenario.limit_delta > 0 ? "better" : scenario.limit_delta < 0 ? "worse" : "same";
  const dirColors = { better: "text-green-600", worse: "text-red-600", same: "text-[#64748B]" };
  const bgColors = { better: "bg-green-50 border-green-100", worse: "bg-red-50 border-red-100", same: "bg-gray-50 border-gray-100" };

  return (
    <div className={cn("rounded-lg border p-3", bgColors[pdDir])} data-testid={`scenario-${scenario.id}`}>
      <p className="text-[10px] font-semibold text-[#502D3C] leading-tight">{scenario.name}</p>
      <p className="text-[8px] text-[#94A3B8] mt-0.5">{scenario.description}</p>
      <div className="mt-2 space-y-1">
        <div className="flex justify-between items-center">
          <span className="text-[9px] text-[#64748B]">PD</span>
          <span className={cn("text-[10px] font-semibold tabular-nums", dirColors[pdDir])}>{scenario.pd}% ({scenario.pd_delta > 0 ? "+" : ""}{scenario.pd_delta})</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-[9px] text-[#64748B]">ECL</span>
          <span className={cn("text-[10px] font-semibold tabular-nums", dirColors[pdDir])}>{fmtCurrency(scenario.ecl)}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-[9px] text-[#64748B]">Limit</span>
          <span className={cn("text-[10px] font-semibold tabular-nums", dirColors[limitDir])}>{fmtCurrency(scenario.credit_limit)}</span>
        </div>
      </div>
      <Badge className="mt-1.5 text-[7px] h-3.5 border" style={{ backgroundColor: RISK_COLORS[scenario.risk_class] + '15', borderColor: RISK_COLORS[scenario.risk_class] + '40', color: RISK_COLORS[scenario.risk_class] }}>{scenario.risk_class}</Badge>
    </div>
  );
}

function LimitRow({ label, value, binding }) {
  return (
    <div className="flex justify-between items-center text-[10px]">
      <span className="text-[#64748B]">{label}</span>
      <span className={cn("font-medium", binding ? "text-[#502D3C]" : "text-[#94A3B8]")}>{value}{binding && <Badge className="ml-1 text-[7px] h-3 bg-[#FFCC1C]/20 text-[#502D3C] border-[#FFCC1C]/40">binding</Badge>}</span>
    </div>
  );
}
